import { Switch as BaseSwitch } from "@base-ui/react/switch";
import classNames from "classnames";
import * as React from "react";

import { Field } from "../Field";

import styles from "./Switch.module.css";

interface SwitchBaseProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
  className?: string;
  /** Points at text explaining the switch, e.g. why it is disabled. */
  "aria-describedby"?: string;
  "data-testid"?: string;
}

/** A switch with its setting written beside it. */
interface LabelledSwitchProps extends SwitchBaseProps {
  /** The setting the switch turns on and off. */
  label: React.ReactNode;
  /** A second line under the label. */
  description?: React.ReactNode;
  "aria-label"?: never;
  "aria-labelledby"?: never;
}

/**
 * A bare switch, for a place that already says what it controls, such as a
 * table cell under an "Enabled" header. It still needs an accessible name.
 */
type UnlabelledSwitchProps = SwitchBaseProps & {
  label?: never;
  description?: never;
} & (
    | { "aria-label": string; "aria-labelledby"?: string }
    | { "aria-label"?: string; "aria-labelledby": string }
  );

export type SwitchProps = LabelledSwitchProps | UnlabelledSwitchProps;

/**
 * An on/off setting that takes effect as a value, not an action: a track and
 * thumb, with a label and an optional description beside it, or on its own
 * with only an accessible name.
 *
 * @example
 * ```tsx
 * <Switch
 *   checked={repair}
 *   onCheckedChange={setRepair}
 *   label="Repair damaged files"
 *   description="Turning it off is faster."
 * />
 * <Switch checked={enabled} onCheckedChange={setEnabled} aria-label="nightly-sync enabled" />
 * ```
 */
export function Switch(props: SwitchProps) {
  const { checked, onCheckedChange, disabled = false, name, className } = props;
  const control = (
    <BaseSwitch.Root
      // A bare switch takes the caller's class; a labelled one gives it to
      // the field around the label.
      className={classNames(
        styles.track,
        props.label === undefined && [styles.standalone, className],
      )}
      checked={checked}
      onCheckedChange={(next) => onCheckedChange?.(next)}
      disabled={disabled}
      name={name}
      aria-label={props["aria-label"]}
      aria-labelledby={props["aria-labelledby"]}
      aria-describedby={props["aria-describedby"]}
      data-testid={props["data-testid"]}
    >
      <BaseSwitch.Thumb className={styles.thumb} />
    </BaseSwitch.Root>
  );

  if (props.label === undefined) {
    return control;
  }

  return (
    <Field.Root disabled={disabled} className={className}>
      <Field.Label className={styles.labelRow}>
        {control}
        <span className={styles.labelText}>{props.label}</span>
      </Field.Label>
      {props.description !== undefined && (
        <Field.Description className={styles.description}>
          {props.description}
        </Field.Description>
      )}
    </Field.Root>
  );
}
